import { api } from '@/lib/api';
import type { LinkCandidate } from '@/lib/types';

/**
 * CLIENT-SIDE LINK CANDIDATES FOR A ROW THAT DOES NOT EXIST YET.
 *
 * `GET /client-deliverables/:id/link-candidates` needs a row id, and the "Add
 * deliverable with no document" form offers the link BEFORE it creates the
 * row. So this builds the same `LinkCandidate[]` from reads that already exist,
 * for one company and one product:
 *
 *  1. The company's deals, quotes and invoices (one page of 100 each — the
 *     server's `perPage` cap; a company with more than that loses the tail).
 *  2. Each document's lines, product-matched. Lost deals (`stage.isLost`, a
 *     flag, never a name) are skipped, as are quote lines already invoiced —
 *     the tracker has dropped those in favour of the invoice line.
 *  3. `onTracker` from the tracker itself (`listClientDeliverables` for the
 *     same company + product): a line is on it when a row names it as its
 *     `line`, or names it as its `dealProductId`. That is the server's own
 *     answer, not a re-derivation of which quote stages it shows.
 *
 * `recommended` is always `false` here; {@link withRecommendation} applies the
 * form's rule on top, because it depends on what the reader types.
 *
 * The server is still the judge: `POST /:id/link` re-checks everything and
 * refuses with `LINK_MISMATCH` / `DEAL_LINE_ALREADY_LINKED` / `LINE_HAS_PIECES`.
 */

const PER_PAGE = 100;
/** Line reads in flight at once — a client with many documents is many requests. */
const CONCURRENCY = 6;

async function mapLimit<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array<R>(items.length);
  let next = 0;
  async function worker() {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await fn(items[index] as T);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

interface LineLike {
  id: number;
  product: { id: number; productName: string };
  project: { id: number; name: string } | null;
  quantity: number;
  cities: { name: string }[];
  regions: { name: string }[];
}

function toCandidate(
  kind: LinkCandidate['kind'],
  documentId: number,
  documentLabel: string,
  documentStatus: string,
  line: LineLike,
  onTracker: boolean,
): LinkCandidate {
  return {
    kind,
    documentId,
    documentLabel,
    documentStatus,
    lineId: line.id,
    product: { id: line.product.id, name: line.product.productName },
    project: line.project ? { id: line.project.id, name: line.project.name } : null,
    quantity: String(line.quantity),
    cities: line.cities.map((city) => city.name),
    regions: line.regions.map((region) => region.name),
    onTracker,
    recommended: false,
  };
}

export async function loadLinkCandidatesFor(
  companyId: number,
  productId: number,
  signal?: AbortSignal,
): Promise<LinkCandidate[]> {
  const [deals, quotes, invoices, tracker] = await Promise.all([
    api.listDeals({ companyId, page: 1, perPage: PER_PAGE }, signal),
    api.listQuotes({ companyId, page: 1, perPage: PER_PAGE }, signal),
    api.listInvoices({ companyId, page: 1, perPage: PER_PAGE }, signal),
    api.listClientDeliverables({ companyId, productId, page: 1, perPage: PER_PAGE }, signal),
  ]);

  const onTracker = new Set<string>();
  for (const row of tracker.data) {
    if (row.line) onTracker.add(`${row.line.kind}:${row.line.id}`);
    if (typeof row.dealProductId === 'number') onTracker.add(`deal:${row.dealProductId}`);
    if (row.linkedDealLine) onTracker.add(`deal:${row.linkedDealLine.lineId}`);
  }

  const dealCandidates = await mapLimit(
    deals.data.filter((deal) => deal.stage?.isLost !== true),
    CONCURRENCY,
    async (deal) => {
      const lines = await api.listDealProducts(deal.id, signal);
      return lines.data
        .filter((line) => line.product.id === productId)
        .map((line) =>
          toCandidate(
            'deal',
            deal.id,
            deal.dealName,
            deal.stage?.name ?? '',
            line,
            onTracker.has(`deal:${line.id}`),
          ),
        );
    },
  );

  const quoteCandidates = await mapLimit(quotes.data, CONCURRENCY, async (quote) => {
    const lines = await api.listQuoteProducts(quote.id, signal);
    const label = quote.quoteNumber ? `${quote.quoteNumber} · ${quote.subject}` : quote.subject;
    return lines.data
      .filter((line) => line.product.id === productId && line.invoiced !== true)
      .map((line) =>
        toCandidate(
          'quote',
          quote.id,
          label,
          quote.stage?.name ?? '',
          line,
          onTracker.has(`quote:${line.id}`),
        ),
      );
  });

  const invoiceCandidates = await mapLimit(invoices.data, CONCURRENCY, async (invoice) => {
    const lines = await api.listInvoiceProducts(invoice.id, signal);
    const label = invoice.xeroInvoiceNumber
      ? `${invoice.xeroInvoiceNumber} · ${invoice.subject}`
      : invoice.subject;
    return lines.data
      .filter((line) => line.product.id === productId)
      .map((line) =>
        toCandidate(
          'invoice',
          invoice.id,
          label,
          invoice.status?.name ?? '',
          line,
          onTracker.has(`invoice:${line.id}`),
        ),
      );
  });

  return [...dealCandidates.flat(), ...quoteCandidates.flat(), ...invoiceCandidates.flat()];
}

/**
 * Marks the lines worth suggesting: not already on the tracker, and — when the
 * reader has typed a promised count — selling exactly that many. With no count
 * typed, a lone line that is not on the tracker is the suggestion. Never more
 * than a hint; nothing is auto-picked.
 */
export function withRecommendation(
  candidates: LinkCandidate[],
  promised: string,
): LinkCandidate[] {
  const count = promised.trim();
  const free = candidates.filter((candidate) => !candidate.onTracker);
  const matches = (candidate: LinkCandidate) =>
    !candidate.onTracker &&
    (/^\d+$/.test(count)
      ? candidate.quantity === count
      : free.length === 1 && free[0] === candidate);
  return candidates.map((candidate) =>
    matches(candidate) === candidate.recommended
      ? candidate
      : { ...candidate, recommended: matches(candidate) },
  );
}

/** "Quote EQ-00012 · Subject" — the line as a sentence names it. */
export function describeCandidate(candidate: LinkCandidate): string {
  const kind =
    candidate.kind === 'deal' ? 'deal' : candidate.kind === 'quote' ? 'quote' : 'invoice';
  return `the ${kind} ${candidate.documentLabel}`;
}
